import { StringFormat } from "@packages/common";
import type { FilterDTO, FilterItemDTO } from "../../search";

enum FuelTypes {
  NATURAL_GAS = "gás natural",
  GASOLINE_ELECTRIC = "gasolina e elétrico",
  ALCOHOL = "álcool",
  GASOLINE_ALCOHOL = "gasolina e álcool",
  GASOLINE = "gasolina",
  ELECTRIC = "elétrico",
  DIESEL = "diesel",
}

enum FuelLabels {
  NATURAL_GAS = "GNV",
  HYBRID = "Híbrido",
  ALCOHOL_ETHANOL = "Álcool (Etanol)",
  FLEX = "Flex",
}

export abstract class FuelTransformer {
  /**
   * Updates the `transformedItems` record with the given label, name, and count.
   * If the label already exists, it appends the name and increases the count.
   *
   * @param {Record<string, FilterItemDTO>} transformedItems - The record of transformed items to update.
   * @param {string} label - The label for the fuel type.
   * @param {string} name - The name to append to the label.
   * @param {number} count - The count to add to the existing count for this label.
   */
  static updateItem(
    transformedItems: Record<string, FilterItemDTO>,
    label: string,
    name: string,
    count: number
  ): void {
    if (!transformedItems[label]) {
      transformedItems[label] = { label, name, count, items: [] };
    } else {
      transformedItems[label].name += `|${name}`;
      transformedItems[label].count += count;
    }
  }

  /**
   * Processes a single fuel item and updates `transformedItems` by mapping known fuel types
   * to display labels or retaining the original label if no mapping exists.
   *
   * @param {Record<string, FilterItemDTO>} transformedItems - The record of transformed items.
   * @param {FilterItemDTO} item - The fuel item to process.
   */
  static processItem(
    transformedItems: Record<string, FilterItemDTO>,
    item: FilterItemDTO
  ): void {
    const { formatSlug } = StringFormat;
    const value = formatSlug(item?.label ?? "");

    if (value.includes(formatSlug(FuelTypes.NATURAL_GAS))) {
      FuelTransformer.updateItem(
        transformedItems,
        FuelLabels.NATURAL_GAS,
        item.name,
        item.count
      );
    } else if (value === formatSlug(FuelTypes.GASOLINE_ELECTRIC)) {
      FuelTransformer.updateItem(
        transformedItems,
        FuelLabels.HYBRID,
        item.name,
        item.count
      );
    } else if (value === formatSlug(FuelTypes.ALCOHOL)) {
      transformedItems[FuelLabels.ALCOHOL_ETHANOL] = {
        label: FuelLabels.ALCOHOL_ETHANOL,
        name: item.name,
        count: item.count,
        items: item.items,
      };
    } else if (value === formatSlug(FuelTypes.GASOLINE_ALCOHOL)) {
      FuelTransformer.updateItem(
        transformedItems,
        FuelLabels.FLEX,
        item.name,
        item.count
      );
    } else if (
      [
        formatSlug(FuelTypes.GASOLINE),
        formatSlug(FuelTypes.ELECTRIC),
        formatSlug(FuelTypes.DIESEL),
      ].includes(value as FuelTypes)
    ) {
      if (!transformedItems[item?.label ?? ""]) {
        transformedItems[item?.label ?? ""] = { ...item };
      }
    }
  }

  /**
   * Transforms a given `FilterDTO` by processing and mapping known fuel types
   * to display labels. Sorts the items by label after transformation.
   *
   * @param {FilterDTO} filter - The filter object to transform.
   * @returns {FilterDTO} - The transformed filter object with sorted items.
   */
  static transform(filter: FilterDTO): FilterDTO {
    if (!filter || filter.name !== "Fuel") return filter;

    const transformedItems: Record<string, FilterItemDTO> = {};

    for (const item of filter.items) {
      FuelTransformer.processItem(transformedItems, item);
    }

    const sortedItems = Object.values(transformedItems).sort((a, b) => {
      const labelA = a?.label ?? "";
      const labelB = b?.label ?? "";
      return labelA.localeCompare(labelB);
    });

    return {
      ...filter,
      items: sortedItems,
    };
  }

  /**
   * Transforms a list of fuel types from a URL by mapping known fuel types to display labels.
   * Groups items by label and concatenates them with `|`.
   *
   * @param {string[]} fuelList - The list of fuel types to transform.
   * @returns {Array<{ value: string, label: string }>} - An array of objects containing the concatenated values and their labels.
   */
  static transformFromUrl(
    fuelList: string[]
  ): { value: string; label: string }[] {
    const transformedItems = new Map<string, string[]>();
    const { formatSlug } = StringFormat;

    for (const fuel of fuelList) {
      const value = formatSlug(fuel);
      let label: string;

      if (value === formatSlug(FuelTypes.GASOLINE_ALCOHOL)) {
        label = FuelLabels.FLEX;
      } else if (value.includes(formatSlug(FuelTypes.NATURAL_GAS))) {
        label = FuelLabels.NATURAL_GAS;
      } else if (value === formatSlug(FuelTypes.GASOLINE_ELECTRIC)) {
        label = FuelLabels.HYBRID;
      } else if (value === formatSlug(FuelTypes.ALCOHOL)) {
        label = FuelLabels.ALCOHOL_ETHANOL;
      } else {
        label = fuel;
      }
      if (!transformedItems.has(label)) {
        transformedItems.set(label, []);
      }
      transformedItems.get(label)?.push(fuel);
    }

    return Array.from(transformedItems.entries()).map(([label, values]) => ({
      value: values.join("|"),
      label,
    }));
  }
}
